import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HomeIcon, FileTextIcon, CreditCardIcon, BuildingIcon, UserIcon, BellIcon, HelpCircleIcon, LogOutIcon, ShieldCheckIcon, CalendarIcon, XIcon } from 'lucide-react';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}
const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  onLogout
}) => {
  const location = useLocation();
  const {
    user
  } = useAuth();
  if (!isOpen) return null;
  const MenuItem = ({
    icon,
    title,
    to,
    badge,
    isActive
  }: {
    icon: React.ReactNode;
    title: string;
    to: string;
    badge?: number;
    isActive: boolean;
  }) => <Link to={to} className={`flex items-center justify-between px-4 py-3 text-sm transition-colors rounded-md ${isActive ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-green-50 hover:text-green-700'}`} onClick={onClose} data-id="element-412">
      <div className="flex items-center" data-id="element-413">
        <span className={`mr-3 ${isActive ? 'text-green-600' : 'text-gray-500'}`} data-id="element-414">
          {icon}
        </span>
        <span data-id="element-415">{title}</span>
      </div>
      {badge ? <span className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full" data-id="element-416">
          {badge}
        </span> : null}
    </Link>;
  return <div className="fixed inset-0 z-50 bg-white flex flex-col h-full" data-id="element-417">
      {/* Header with close button */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-418">
        <div className="flex items-center space-x-3" data-id="element-419">
          <div className="bg-green-50 p-1 rounded-md flex items-center justify-center" data-id="element-420">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-8 w-8" data-id="element-421" />
          </div>
          <div data-id="element-422">
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-423">Edo State</h2>
            <p className="text-xs text-gray-500" data-id="element-424">E-Services Portal</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none" aria-label="Close menu" data-id="element-425">
          <XIcon className="h-6 w-6" data-id="element-426" />
        </button>
      </div>
      {/* User Profile */}
      <div className="p-4 border-b border-gray-200" data-id="element-427">
        <div className="flex items-center space-x-3" data-id="element-428">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold" data-id="element-429">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div data-id="element-430">
            <p className="text-sm font-medium text-gray-900" data-id="element-431">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500" data-id="element-432">
              {user?.email || 'user@example.com'}
            </p>
          </div>
        </div>
      </div>
      {/* Navigation Items - scrollable area */}
      <div className="flex-1 overflow-y-auto py-4 px-3" data-id="element-433">
        <div className="space-y-1" data-id="element-434">
          <MenuItem icon={<HomeIcon size={20} data-id="element-436" />} title="Dashboard" to="/dashboard" isActive={location.pathname === '/dashboard'} data-id="element-435" />
          <MenuItem icon={<FileTextIcon size={20} data-id="element-438" />} title="My Applications" to="/my-applications" isActive={location.pathname === '/my-applications'} badge={2} data-id="element-437" />
          <MenuItem icon={<CreditCardIcon size={20} data-id="element-440" />} title="Payment History" to="/payment-history" isActive={location.pathname === '/payment-history'} data-id="element-439" />
          <MenuItem icon={<BuildingIcon size={20} data-id="element-442" />} title="Government Services" to="/mdas" isActive={location.pathname === '/mdas'} data-id="element-441" />
          <MenuItem icon={<CalendarIcon size={20} data-id="element-444" />} title="Track Applications" to="/track-application" isActive={location.pathname === '/track-application'} data-id="element-443" />
        </div>
        <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-445">
          <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-446">
            Account
          </h3>
          <div className="space-y-1" data-id="element-447">
            <MenuItem icon={<UserIcon size={20} data-id="element-449" />} title="My Profile" to="/profile" isActive={location.pathname === '/profile'} data-id="element-448" />
            <MenuItem icon={<BellIcon size={20} data-id="element-451" />} title="Notifications" to="/notifications" isActive={location.pathname === '/notifications'} badge={1} data-id="element-450" />
            <MenuItem icon={<HelpCircleIcon size={20} data-id="element-453" />} title="Help & Support" to="/support" isActive={location.pathname === '/support'} data-id="element-452" />
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-gray-200" data-id="element-454">
        <div className="flex items-center px-4 py-2 mb-4 bg-green-50 text-green-700 rounded-md" data-id="element-455">
          <ShieldCheckIcon size={18} className="mr-2" data-id="element-456" />
          <span className="text-xs font-medium" data-id="element-457">Secure Connection</span>
        </div>
        <button onClick={() => {
        onLogout();
        onClose();
      }} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-458">
          <LogOutIcon size={20} className="mr-3" data-id="element-459" />
          Logout
        </button>
      </div>
    </div>;
};
export default MobileMenu;